import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import DoorFiligree from "./DoorFiligree";
import { WEDDING_CONFIG, getGuestName } from "../config";

export default function DoorCover({ onOpen }) {
  const [opening, setOpening] = useState(false);
  const [closed, setClosed] = useState(true);
  const guestName = getGuestName();

  useEffect(() => {
    if (closed) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [closed]);

  const fireConfetti = () => {
    const end = Date.now() + 1800;
    const colors = ["#C5A059", "#E8D6A8", "#B38F4D", "#FDFBF7"];
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0, y: 0.7 }, colors, scalar: 1.1 });
      confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors, scalar: 1.1 });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  };

  const handleOpen = () => {
    if (opening) return;
    setOpening(true);
    fireConfetti();
    onOpen?.();
    setTimeout(() => setClosed(false), 2200);
  };

  return (
    <AnimatePresence>
      {closed && (
        <motion.div
          data-testid="door-cover"
          className="fixed inset-0 z-50 overflow-hidden"
          style={{ perspective: "1400px" }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-ivory via-cream to-ivory" />

          {/* Sneak peek behind doors */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center max-w-md px-6">
              {guestName && (
                <p className="text-xs tracking-[0.3em] uppercase text-charcoal/60 mb-4" data-testid="guest-name-cover">
                  Kepada Yth.<br />
                  <span className="font-serif italic text-lg text-charcoal/80 normal-case tracking-normal mt-1 inline-block">{guestName}</span>
                </p>
              )}
              <p className="font-script text-5xl gold-text mb-3">The Wedding of</p>
              <h1 className="font-serif text-7xl md:text-8xl text-charcoal tracking-tight">
                {WEDDING_CONFIG.groom.initial} <span className="font-script gold-text text-6xl align-middle">&</span> {WEDDING_CONFIG.bride.initial}
              </h1>
              <div className="divider mt-6">
                <span className="text-xs tracking-[0.3em] uppercase">Save the date</span>
              </div>
            </div>
          </div>

          {/* LEFT DOOR */}
          <motion.div
            className="door-panel absolute top-0 left-0 h-full w-1/2 origin-left"
            style={{ transformStyle: "preserve-3d" }}
            initial={{ rotateY: 0 }}
            animate={opening ? { rotateY: -105 } : { rotateY: 0 }}
            transition={{ duration: 1.8, ease: [0.25, 1, 0.5, 1] }}
          >
            <DoorFiligree />
            <div className="door-hinge" style={{ top: "12%", left: 4 }} />
            <div className="door-hinge" style={{ top: "50%", left: 4 }} />
            <div className="door-hinge" style={{ top: "85%", left: 4 }} />
          </motion.div>

          {/* RIGHT DOOR */}
          <motion.div
            className="door-panel absolute top-0 right-0 h-full w-1/2 origin-right"
            style={{ transformStyle: "preserve-3d" }}
            initial={{ rotateY: 0 }}
            animate={opening ? { rotateY: 105 } : { rotateY: 0 }}
            transition={{ duration: 1.8, ease: [0.25, 1, 0.5, 1] }}
          >
            <DoorFiligree mirror />
            <div className="door-hinge" style={{ top: "12%", right: 4 }} />
            <div className="door-hinge" style={{ top: "50%", right: 4 }} />
            <div className="door-hinge" style={{ top: "85%", right: 4 }} />
          </motion.div>

          {/* CENTER HANDLE / MEDALLION */}
          <motion.button
            data-testid="door-cover-handle"
            onClick={handleOpen}
            aria-label="Buka undangan"
            className="absolute z-20 group"
            style={{ left: "50%", top: "50%", width: "112px", height: "112px", marginLeft: "-56px", marginTop: "-56px", padding: 0, border: "none", background: "transparent", cursor: "pointer" }}
            animate={opening ? { scale: 0, opacity: 0 } : { scale: 1, opacity: 1 }}
            transition={{ duration: 0.4 }}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="relative w-28 h-28 rounded-full flex items-center justify-center bg-gradient-to-br from-cream via-gold-light to-gold animate-glow-pulse">
              <div className="absolute inset-2 rounded-full border border-gold/50" />
              <div className="absolute inset-4 rounded-full border border-gold/30" />
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-white via-cream to-gold-light" style={{ boxShadow: "inset -3px -3px 8px rgba(197,160,89,0.4), inset 3px 3px 8px rgba(255,255,255,0.9)" }} />
            </div>
          </motion.button>

          {/* Labels positioned independently below medallion */}
          <motion.div
            className="absolute z-20 text-center whitespace-nowrap pointer-events-none"
            style={{ left: "50%", top: "50%", transform: "translate(-50%, calc(56px + 24px))" }}
            animate={opening ? { opacity: 0 } : { opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-xs tracking-[0.3em] uppercase text-charcoal/70 mb-1">Tekan untuk membuka</p>
            <p className="font-serif italic text-gold text-lg">Undangan Pernikahan</p>
          </motion.div>

          <div className="absolute inset-0 pointer-events-none" style={{
            background: "radial-gradient(ellipse at center, transparent 40%, rgba(44,42,41,0.15) 100%)"
          }} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
